import { useState, useEffect, useRef } from 'react';
import PropTypes from 'prop-types';
import { Icon } from '@evertrue/et-components';
import { RichTextEditor } from '@evertrue/sdk';
import FileUpload from 'components/emailer/components/file-upload';
import UploadedFiles from 'components/emailer/components/uploaded-files';
import useFileUploader from 'components/emailer/hooks/use-file-uploader';
import { DYNAMIC_FIELDS, MAX_AUTOMATED_ATTACHMENT_BYTES } from '../../../utils/cadence-constants';
import sanitizeHtml from '../../../utils/sanitize-html';
import { filesToEventConfigAttachments, eventConfigAttachmentsToUploader } from '../../../utils/cadence-attachments';

/**
 * Email Content tab for the cadence step drawer.
 * Four numbered card sections: Select Template → Email Copy → Signature → Preview.
 * Drift detection banner appears when the user edits a template-linked step.
 */
const CadenceEmailContent = ({
  subject,
  onSubjectChange,
  body,
  onBodyChange,
  template,
  templateOriginalSubject,
  templateOriginalBody,
  onTemplateSelect,
  onTemplateUnlink,
  signature,
  onSignatureChange,
  outreachTemplates,
  signatures,
  stepId,
  initialAttachments,
  onAttachmentsChange,
  isAutomated,
  editorKey,
}) => {
  const [driftDismissed, setDriftDismissed] = useState(false);

  // Each email step owns its own uploader instance (this component is keyed by
  // step id upstream, so it remounts per step). It seeds from the step's saved
  // attachments — the hook re-resolves a fresh signed download URL per object
  // key on mount — and we mirror any change back up into the step's
  // event_config so it persists on save.
  const { files, removeFile, onImageUpload, onFileUpload, isReuploading } = useFileUploader(
    eventConfigAttachmentsToUploader(initialAttachments),
    stepId || null
  );

  // Hold the latest callback in a ref so the mirror effect can depend only on
  // file state, not on the parent's (re-created each render) handler identity.
  const onAttachmentsChangeRef = useRef(onAttachmentsChange);
  onAttachmentsChangeRef.current = onAttachmentsChange;

  // Skip the initial empty render and the rehydration window so we never
  // overwrite saved attachments with [] before the uploader has reloaded them.
  const didInitRef = useRef(false);
  useEffect(() => {
    if (!didInitRef.current) {
      didInitRef.current = true;
      return;
    }
    if (isReuploading) return;
    onAttachmentsChangeRef.current?.(filesToEventConfigAttachments(files));
  }, [files, isReuploading]);

  // Reset dismissed state when the linked template changes
  const templateId = template?.id;
  useEffect(() => {
    setDriftDismissed(false);
  }, [templateId]);

  const hasDrift =
    template &&
    templateOriginalSubject != null &&
    (subject !== templateOriginalSubject || body !== templateOriginalBody);

  const showDriftBanner = hasDrift && !driftDismissed;

  const selectedSignature = (signatures || []).find((s) => s.id === signature?.id) || null;

  const handleTemplateChange = (e) => {
    const val = e.target.value;
    if (!val) {
      onTemplateUnlink();
      return;
    }
    const t = (outreachTemplates || []).find((tmpl) => String(tmpl.id) === val);
    if (!t) return;
    onTemplateSelect(t);
    // Jodit ignores controlled value changes — force the editor DOM directly
    const templateBody = t.templateBody || t.dynamicTemplate || '';
    const editorEl = document.querySelector('.jodit-wysiwyg');
    if (editorEl) {
      editorEl.innerHTML = templateBody;
      const placeholder = editorEl.closest('.jodit-container')?.querySelector('.jodit-placeholder');
      if (placeholder) placeholder.style.display = 'none';
    }
  };

  // Compute which non-inline files pushed the running total over the Postmark limit.
  // These files are still uploaded and included in the submission payload so the backend
  // returns ATTACHMENT_TOO_LARGE at activation time rather than silently dropping them.
  // Maps file id → running total at the point it went over, for display in the warning.
  const overLimitMap = (() => {
    if (!isAutomated) return new Map();
    let running = 0;
    const map = new Map();
    for (const f of files.filter((f) => !f.isInline)) {
      running += f.file?.size || 0;
      if (running > MAX_AUTOMATED_ATTACHMENT_BYTES) map.set(f.id, running);
    }
    return map;
  })();
  const validDisplayFiles = files.filter((f) => !overLimitMap.has(f.id));
  const overLimitFiles = files.filter((f) => overLimitMap.has(f.id));

  const handleSignatureChange = (e) => {
    const val = e.target.value;
    if (!val) {
      onSignatureChange(null);
      return;
    }
    const sig = (signatures || []).find((s) => String(s.id) === val);
    if (sig) onSignatureChange(sig);
  };

  return (
    <div className="cadence-email-content">
      {/* ── 1 · Select a Template ─────────────────────────────── */}
      <div className="cadence-email-content--section">
        <div className="cadence-email-content--section-header">
          <span className="cadence-email-content--section-num">1</span>
          <div className="cadence-email-content--section-header-text">
            <span className="cadence-email-content--section-title">Select a Template</span>
            <span className="cadence-email-content--section-subtitle">
              Optionally start from a saved template. You can customize the copy after selecting.
            </span>
          </div>
        </div>
        <div className="cadence-email-content--section-body">
          <select
            className="create-cadence-drawer--select"
            value={template?.id != null ? String(template.id) : ''}
            onChange={handleTemplateChange}
          >
            <option value="">No template</option>
            {(outreachTemplates || []).map((t) => (
              <option key={t.id} value={String(t.id)}>
                {t.templateName}
              </option>
            ))}
          </select>
        </div>
      </div>

      {/* ── 2 · Email Copy ────────────────────────────────────── */}
      <div className="cadence-email-content--section">
        <div className="cadence-email-content--section-header">
          <span className="cadence-email-content--section-num">2</span>
          <div className="cadence-email-content--section-header-text">
            <span className="cadence-email-content--section-title">Email Copy</span>
            <span className="cadence-email-content--section-subtitle">
              Write or refine the email content for this step.
            </span>
          </div>
        </div>
        <div className="cadence-email-content--section-body">
          <div className="cadence-email-content--field">
            <label className="cadence-email-content--label">Subject</label>
            <input
              type="text"
              className="create-cadence-drawer--input"
              placeholder="Enter a subject line..."
              value={subject || ''}
              onChange={(e) => onSubjectChange(e.target.value)}
            />
          </div>
          <div className="cadence-email-content--field">
            <RichTextEditor
              key={editorKey}
              value={body || ''}
              onChange={onBodyChange}
              dynamicFields={DYNAMIC_FIELDS}
              onImageUpload={onImageUpload}
              onFileUpload={onFileUpload}
              placeholder="Start typing your email..."
              additionalConfig={{ height: '260px', toolbarAdaptive: false }}
            />
          </div>
          <div className="create-cadence-drawer--email-toolbar">
            <FileUpload onFileChange={onFileUpload} allowMultiple={true} />
          </div>
          {files.some((f) => !f.isInline) && (
            <div className="create-cadence-drawer--attachments">
              <UploadedFiles files={validDisplayFiles} removeFile={removeFile} />
              {overLimitFiles.length > 0 && (
                <div className="uploaded-files--container">
                  {overLimitFiles.map((f) => (
                    <div key={f.id} className="uploaded-file uploaded-file--error">
                      <div style={{ display: 'flex', alignItems: 'center' }}>
                        <Icon icon="alert-circle" style={{ color: '#c0392b' }} />
                        <div style={{ marginLeft: '10px' }}>
                          <p>{f.file?.name}</p>
                          <small style={{ color: '#c0392b' }}>
                            Attachment warning: Total attachment size exceeds the 7MB limit (
                            {(overLimitMap.get(f.id) / 1_000_000).toFixed(1)} MB total).
                          </small>
                        </div>
                      </div>
                      <Icon
                        icon="close"
                        onClick={() => removeFile(f.id)}
                        isReachable={true}
                        onKeyUp={() => removeFile(f.id)}
                      />
                    </div>
                  ))}
                </div>
              )}
            </div>
          )}
        </div>
      </div>

      {/* ── Drift Banner ──────────────────────────────────────── */}
      {showDriftBanner && (
        <div className="cadence-email-content--drift-banner">
          <div className="cadence-email-content--drift-body">
            <Icon icon="info" className="cadence-email-content--drift-icon" />
            <div className="cadence-email-content--drift-text">
              <p className="cadence-email-content--drift-title">
                This looks different from the <strong>{template.name}</strong> template
              </p>
              <p className="cadence-email-content--drift-hint">
                Unlinking will not alter the subject or body you&apos;ve already written.
              </p>
            </div>
          </div>
          <div className="cadence-email-content--drift-actions">
            <button type="button" className="cadence-email-content--drift-unlink" onClick={onTemplateUnlink}>
              Unlink from template
            </button>
            <button
              type="button"
              className="cadence-email-content--drift-dismiss"
              title="Dismiss"
              onClick={() => setDriftDismissed(true)}
            >
              <Icon icon="close" />
            </button>
          </div>
        </div>
      )}

      {/* ── 3 · Signature ─────────────────────────────────────── */}
      <div className="cadence-email-content--section">
        <div className="cadence-email-content--section-header">
          <span className="cadence-email-content--section-num">3</span>
          <div className="cadence-email-content--section-header-text">
            <span className="cadence-email-content--section-title">Signature</span>
            <span className="cadence-email-content--section-subtitle">
              Choose which signature to include. This won&apos;t affect your template or email copy.
            </span>
          </div>
        </div>
        <div className="cadence-email-content--section-body">
          <select
            className="create-cadence-drawer--select"
            value={signature?.id != null ? String(signature.id) : ''}
            onChange={handleSignatureChange}
          >
            <option value="">No signature</option>
            {(signatures || []).map((s) => (
              <option key={s.id} value={String(s.id)}>
                {s.signature_name}
              </option>
            ))}
          </select>
        </div>
      </div>

      {/* ── 4 · Preview ───────────────────────────────────────── */}
      <div className="cadence-email-content--section">
        <div className="cadence-email-content--section-header">
          <span className="cadence-email-content--section-num">4</span>
          <div className="cadence-email-content--section-header-text">
            <span className="cadence-email-content--section-title">Preview</span>
            <span className="cadence-email-content--section-subtitle">
              Read-only view of how this email will appear when sent.
            </span>
          </div>
        </div>
        <div className="cadence-email-content--section-body">
          <div className="cadence-email-content--preview">
            {subject || body ? (
              <>
                {subject ? (
                  <div className="cadence-email-content--preview-subject">{subject}</div>
                ) : (
                  <div className="cadence-email-content--preview-subject is-empty">No subject yet</div>
                )}
                <div
                  className="cadence-email-content--preview-body"
                  dangerouslySetInnerHTML={{ __html: sanitizeHtml(body || '') }}
                />
                {selectedSignature?.signature_body && (
                  <>
                    <hr className="cadence-email-content--preview-divider" />
                    <div
                      className="cadence-email-content--preview-signature"
                      dangerouslySetInnerHTML={{ __html: sanitizeHtml(selectedSignature.signature_body) }}
                    />
                  </>
                )}
              </>
            ) : (
              <div className="cadence-email-content--preview-empty">
                <p>No subject yet</p>
                <p>No email copy yet</p>
              </div>
            )}
          </div>
        </div>
      </div>
    </div>
  );
};

CadenceEmailContent.propTypes = {
  subject: PropTypes.string,
  onSubjectChange: PropTypes.func.isRequired,
  body: PropTypes.string,
  onBodyChange: PropTypes.func.isRequired,
  template: PropTypes.shape({
    id: PropTypes.oneOfType([PropTypes.string, PropTypes.number]),
    name: PropTypes.string,
  }),
  templateOriginalSubject: PropTypes.string,
  templateOriginalBody: PropTypes.string,
  onTemplateSelect: PropTypes.func.isRequired,
  onTemplateUnlink: PropTypes.func.isRequired,
  signature: PropTypes.shape({
    id: PropTypes.oneOfType([PropTypes.string, PropTypes.number]),
    signature_name: PropTypes.string,
  }),
  onSignatureChange: PropTypes.func.isRequired,
  outreachTemplates: PropTypes.array,
  signatures: PropTypes.array,
  stepId: PropTypes.oneOfType([PropTypes.string, PropTypes.number]),
  initialAttachments: PropTypes.array,
  onAttachmentsChange: PropTypes.func.isRequired,
  isAutomated: PropTypes.bool,
  editorKey: PropTypes.string,
};

export default CadenceEmailContent;
